import type { ColumnBaseConfig } from 'drizzle-orm/column'
import type { PgColumn } from 'drizzle-orm/pg-core'
import { SQL, StringChunk } from 'drizzle-orm/sql'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'
import { z } from 'zod'
import {
  backboneStatusCurrent,
  deviceStatusCurrent,
  deviceStatusPolls,
  edgeVendor,
  locations,
  locationTypeEnum,
  networkProfiles,
  nodes,
  orgs,
  paymentsProfiles,
  reservations,
  switchVendor,
  terminals,
  tunnelStatusCurrent,
  tunnelStatusPolls,
  vendorSiteNetworks,
  vendorSites,
  wanData,
} from './schema'

type NestedKeyOf<ObjectType> = {
  [Key in keyof ObjectType & (string | number)]: ObjectType[Key] extends Array<infer ArrayType>
    ? `${Key}` | `${Key}.${number}` | (ArrayType extends object ? `${Key}.${number}.${NestedKeyOf<ArrayType>}` : never)
    : ObjectType[Key] extends object
      ? `${Key}` | `${Key}.${NestedKeyOf<ObjectType[Key]>}`
      : `${Key}`
}[keyof ObjectType & (string | number)]

// Helper type to recursively infer the type at a given path.
type AtPath<T, Path extends string> = Path extends `${infer Key}.${infer Rest}`
  ? Key extends keyof T
    ? Rest extends NestedKeyOf<T[Key]>
      ? AtPath<T[Key], Rest>
      : never
    : Key extends `${number}`
      ? T extends Array<infer ArrayType>
        ? ArrayType extends object
          ? AtPath<ArrayType, Rest>
          : never
        : never
      : never
  : Path extends keyof T
    ? T[Path]
    : never

export function jsonbField<
  T extends PgColumn<ColumnBaseConfig<'json', 'PgJsonb'>>,
  P extends NestedKeyOf<T['_']['data']>,
>(column: T, path: P) {
  const pathParts = path.split('.')
  let concatenatedSql = ''

  pathParts.forEach((part, index) => {
    if (index === pathParts.length - 1) {
      concatenatedSql += ` ->> '${part}'`
      return
    }
    concatenatedSql += `-> '${part}'`
  })

  return new SQL<AtPath<T['_']['data'], P>>([column, new StringChunk(concatenatedSql)])
}

const genericBaseSchema = z.string().or(z.number()).or(z.boolean()).or(z.null()).or(z.array(z.string()))

const genericRecordSchema = z.record(
  z.string(),
  genericBaseSchema.or(z.record(z.string(), genericBaseSchema)).or(z.array(genericBaseSchema)),
)
export type GenericRecord = z.infer<typeof genericRecordSchema>

export const genericJsonbSchema = z.record(z.string(), genericRecordSchema)
export type GenericJsonb = z.infer<typeof genericJsonbSchema>

export const orgMetadataSchema = z.record(
  z.string(),
  z.string().or(z.number()).or(z.boolean()).or(z.null()).or(z.array(z.string())),
)
export type OrgMetadata = z.infer<typeof orgMetadataSchema>

export const orgExtremeDataSchema = z.object({
  orgId: z.string().optional(),
  apiUser: z.string(),
  apiPassword: z.string(),
})
export type OrgExtremeData = z.infer<typeof orgExtremeDataSchema>

export const orgCradlepointDataSchema = z.object({
  apiId: z.string(),
  apiKey: z.string(),
  ecmApiId: z.string(),
  ecmApiKey: z.string(),
})
export type OrgCradlepointData = z.infer<typeof orgCradlepointDataSchema>

export const orgCfDataSchema = z.object({
  accountId: z.string(),
  apiEmail: z.string().optional(),
  apiKey: z.string().optional(),
  apiToken: z.string().optional(),
  schemaStart: z.string().optional(),
  schemaStartDev: z.string().optional(),
})
export type OrgCfData = z.infer<typeof orgCfDataSchema>

export const orgMerakiDataSchema = z.object({
  orgId: z.string(),
  apiKey: z.string(),
  schemaStart: z.string().optional(),
  schemaStartDev: z.string().optional(),
})
export type OrgMerakiData = z.infer<typeof orgMerakiDataSchema>

export const orgWansDataSchema = z.object({
  orgId: z.string(),
  apiKey: z.string(),
})

export const orgIntuneDataSchema = z.object({
  tenantId: z.string(),
  clientId: z.string(),
  clientSecret: z.string(),
  groupIds: z.array(z.string()),
})
export type OrgIntuneData = z.infer<typeof orgIntuneDataSchema>

export const clerkOrgMetadataSchema = z.object({
  mid: z.string().optional(),
  title: z.string().optional(),
})

export const orgPrivateMetadataSchema = clerkOrgMetadataSchema
export const orgPublicMetadataSchema = clerkOrgMetadataSchema
export type OrgPrivateMetadata = z.infer<typeof orgPrivateMetadataSchema>
export type OrgPublicMetadata = z.infer<typeof orgPublicMetadataSchema>
export const orgSchema = createSelectSchema(orgs)
export const orgsSchema = z.array(orgSchema)
export const insertOrgSchema = createInsertSchema(orgs)
export const updateOrgSchema = createUpdateSchema(orgs)
export type SelectOrg = z.infer<typeof orgSchema>
export type InsertOrg = z.infer<typeof insertOrgSchema>
export type UpdateOrg = z.infer<typeof updateOrgSchema>
export type SelectOrgs = z.infer<typeof orgsSchema>

export const locationTypeSchema = z.enum(locationTypeEnum.enumValues)
export type LocationType = z.infer<typeof locationTypeSchema>
export const edgeVendorSchema = z.enum(edgeVendor.enumValues)
export type EdgeVendor = z.infer<typeof edgeVendorSchema>
export const switchVendorSchema = z.enum(switchVendor.enumValues)
export type SwitchVendor = z.infer<typeof switchVendorSchema>

export const selectLocationSchema = createSelectSchema(locations)
export const updateLocationSchema = createUpdateSchema(locations)
export const insertLocationSchema = createInsertSchema(locations)
export type SelectInternalLocation = z.infer<typeof selectLocationSchema>
export type UpdateInternalLocation = z.infer<typeof updateLocationSchema>
export type InsertInternalLocation = z.infer<typeof insertLocationSchema>

export const paymentsProfilesSchema = createSelectSchema(paymentsProfiles)
export const insertPaymentsProfilesSchema = createInsertSchema(paymentsProfiles)
export const updatePaymentsProfilesSchema = createUpdateSchema(paymentsProfiles).omit({
  createdAt: true,
  updatedAt: true,
})
export type SelectPaymentsProfile = z.infer<typeof paymentsProfilesSchema>
export type InsertPaymentsProfile = z.infer<typeof insertPaymentsProfilesSchema>
export type UpdatePaymentsProfile = z.infer<typeof updatePaymentsProfilesSchema>

export const selectTerminalSchema = createSelectSchema(terminals)
export const updateTerminalSchema = createUpdateSchema(terminals)
export const insertTerminalSchema = createInsertSchema(terminals)
export type SelectInternalTerminal = z.infer<typeof selectTerminalSchema>
export type UpdateInternalTerminal = z.infer<typeof updateTerminalSchema>
export type InsertInternalTerminal = z.infer<typeof insertTerminalSchema>

export const networkProfileSchema = createSelectSchema(networkProfiles)
export const insertNetworkProfileSchema = createInsertSchema(networkProfiles)
export const updateNetworkProfileSchema = createUpdateSchema(networkProfiles)
export type SelectNetworkProfile = z.infer<typeof networkProfileSchema>
export type InsertNetworkProfile = z.infer<typeof insertNetworkProfileSchema>
export type UpdateNetworkProfile = z.infer<typeof updateNetworkProfileSchema>

export const vendorSiteSchema = createSelectSchema(vendorSites)
export const insertVendorSiteSchema = createInsertSchema(vendorSites)
export const updateVendorSiteSchema = createUpdateSchema(vendorSites)
export type SelectVendorSite = z.infer<typeof vendorSiteSchema>
export type InsertVendorSite = z.infer<typeof insertVendorSiteSchema>
export type UpdateVendorSite = z.infer<typeof updateVendorSiteSchema>

export const vendorSiteNetworkSchema = createSelectSchema(vendorSiteNetworks)
export const insertVendorSiteNetworkSchema = createInsertSchema(vendorSiteNetworks)
export const updateVendorSiteNetworkSchema = createUpdateSchema(vendorSiteNetworks)
export type SelectVendorSiteNetwork = z.infer<typeof vendorSiteNetworkSchema>
export type InsertVendorSiteNetwork = z.infer<typeof insertVendorSiteNetworkSchema>
export type UpdateVendorSiteNetwork = z.infer<typeof updateVendorSiteNetworkSchema>

export const nodeSchema = createSelectSchema(nodes)
export const insertNodeSchema = createInsertSchema(nodes)
export const updateNodeSchema = createUpdateSchema(nodes)
export type SelectNode = z.infer<typeof nodeSchema>
export type InsertNode = z.infer<typeof insertNodeSchema>
export type UpdateNode = z.infer<typeof updateNodeSchema>

export const wanDataSchema = createSelectSchema(wanData)
export type SelectWanData = z.infer<typeof wanDataSchema>

export const reservationSchema = createSelectSchema(reservations)
export const insertReservationSchema = createInsertSchema(reservations)
export const updateReservationSchema = createUpdateSchema(reservations)
export type SelectReservation = z.infer<typeof reservationSchema>
export type InsertReservation = z.infer<typeof insertReservationSchema>
export type UpdateReservation = z.infer<typeof updateReservationSchema>

export const selectNodeFullSchema = nodeSchema.extend({
  org: orgSchema.nullable().optional(),
  networkProfile: networkProfileSchema
    .extend({
      location: selectLocationSchema.nullable().optional(),
    })
    .nullable()
    .optional(),
  site: vendorSiteSchema.nullable().optional(),
})
export const selectNodesFullSchema = z.array(selectNodeFullSchema)
export type SelectNodeFull = z.infer<typeof selectNodeFullSchema>
export type SelectNodesFull = z.infer<typeof selectNodesFullSchema>

/**
 * Slim list-item schema for the server-paginated `GET /v1/nodes` list (#837 —
 * the full-relation join produced a ~22.7MB response for ~2,800 nodes). Keeps
 * every node scalar column (the managedDevices table + expand/detail views read
 * many of them, incl. extraData), but trims the heavy relations to only what the
 * UI actually uses:
 *   - `org` -> { name, slug }        (table Org column + WorkstationDetails banner)
 *   - `networkProfile.location` -> { id, aptosStoreCode }
 *       (columns.tsx workstation deep-link + row-actions unassign/replace)
 * `site` and all other networkProfile columns are dropped (unused by the UI).
 * Keep in sync with the projection in the listNodes handler.
 */
export const selectNodeListItemSchema = nodeSchema.extend({
  org: orgSchema.pick({ name: true, slug: true }).nullable(),
  networkProfile: z
    .object({
      location: selectLocationSchema.pick({ id: true, aptosStoreCode: true }).nullable(),
    })
    .nullable(),
})
export type SelectNodeListItem = z.infer<typeof selectNodeListItemSchema>

/**
 * Paginated envelope for `GET /v1/nodes` (server-side list, #837).
 */
export const paginatedNodeListSchema = z.object({
  items: z.array(selectNodeListItemSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  totalPages: z.number().int().nonnegative(),
})
export type PaginatedNodeList = z.infer<typeof paginatedNodeListSchema>

export const vendorSiteFullSchema = vendorSiteSchema.extend({
  nodes: nodeSchema.array().optional(),
  vendorSiteNetworks: vendorSiteNetworkSchema
    .extend({
      reservations: reservationSchema.array().optional(),
    })
    .array()
    .optional(),
  networkProfile: networkProfileSchema
    .extend({
      location: selectLocationSchema.nullable().optional(),
    })
    .nullable()
    .optional(),
})
export type SelectVendorSiteFull = z.infer<typeof vendorSiteFullSchema>

export const vendorSiteNetworkFullSchema = vendorSiteNetworkSchema.extend({
  reservations: reservationSchema.array().optional(),
  vendorSite: vendorSiteSchema.extend({
    nodes: nodeSchema.array().optional(),
    location: selectLocationSchema.nullable(),
  }),
})
export type SelectVendorSiteNetworkFull = z.infer<typeof vendorSiteNetworkFullSchema>

export const networkProfileFullSchema = networkProfileSchema.extend({
  // Changed from vendorSite (singular) to vendorSites (plural) to match database relation
  vendorSites: vendorSiteSchema
    .extend({
      vendorSiteNetworks: vendorSiteNetworkSchema
        .extend({
          reservations: reservationSchema.array().optional(),
        })
        .array()
        .optional(),
    })
    .array()
    .optional(),
  nodes: nodeSchema.array().optional(),
  // Embedded only when getLocationsLocal is called with withWanData (default off),
  // so the response carries the location's WAN circuits (carrier, assigned IP,
  // type, status) without changing the payload for existing callers.
  wanData: wanDataSchema.array().optional(),
})
export type SelectNetworkProfileFull = z.infer<typeof networkProfileFullSchema>

export const selectTerminalWithLocationSchema = selectTerminalSchema.extend({
  location: selectLocationSchema.nullable(),
})
export type SelectInternalTerminalWithLocation = z.infer<typeof selectTerminalWithLocationSchema>

export const selectTerminalJoinedLocationSchema = z
  .object({
    terminals: selectTerminalSchema,
    locations: selectLocationSchema.nullable(),
  })
  .array()
export type SelectInternalTerminalJoinedLocation = z.infer<typeof selectTerminalJoinedLocationSchema>

/**
 * Slim projection of the terminals + locations join used by the terminals list
 * endpoint (`GET /v1/terminals`). The full join selects every column of both
 * tables for the entire fleet, producing a ~2.8MB response (issue #381). The
 * terminals page and POS flows only render the fields below, so the list query
 * projects to exactly these columns. Keep this in sync with the columns
 * rendered by the terminals table and POS terminal pickers.
 */
export const selectTerminalListItemSchema = selectTerminalSchema.pick({
  id: true,
  workstationId: true,
  model: true,
  serialNumber: true,
  name: true,
  companyId: true,
  merchantId: true,
  adyenStoreId: true,
  firmwareVersion: true,
  status: true,
  lastActivityAt: true,
  lastTransactionAt: true,
})
export type SelectInternalTerminalListItem = z.infer<typeof selectTerminalListItemSchema>

export const selectTerminalListJoinedLocationSchema = z
  .object({
    terminals: selectTerminalListItemSchema,
    // Drizzle returns `null` for the whole joined object on an unmatched
    // leftJoin row; allow the inner field to be null too in case the join
    // grouping yields `{ aptosStoreCode: null }` instead.
    locations: selectLocationSchema
      .pick({ aptosStoreCode: true })
      .extend({ aptosStoreCode: z.string().nullable() })
      .nullable(),
  })
  .array()
export type SelectInternalTerminalListJoinedLocation = z.infer<typeof selectTerminalListJoinedLocationSchema>

/**
 * Paginated envelope for the terminals list endpoint (`GET /v1/terminals`).
 * The list is server-paginated/filtered/sorted (#381 — the banner-wide fleet is
 * ~1,900 rows / ~765KB unpaginated), so a response carries one page of joined
 * rows plus the total matching-row count for the client pager. `items` reuses
 * the slim joined-list schema above.
 */
export const paginatedTerminalListSchema = z.object({
  items: selectTerminalListJoinedLocationSchema,
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  totalPages: z.number().int().nonnegative(),
})
export type PaginatedTerminalList = z.infer<typeof paginatedTerminalListSchema>

export const selectLocationFullSchema = selectLocationSchema.extend({
  org: orgSchema.nullable(),
  terminals: z.array(selectTerminalSchema).optional(),
  networkProfiles: z.array(networkProfileFullSchema).nullable().optional(),
  paymentsProfiles: z.array(paymentsProfilesSchema).nullable().optional(),
  // Scalar terminal count for list views that show a count but don't need the
  // full terminal rows embedded (#loader-latency: getLocationsLocal
  // withTerminalCount). Populated only when requested; absent otherwise.
  terminalCount: z.number().int().nonnegative().optional(),
})
export const selectLocationsFullSchema = z.array(selectLocationFullSchema)
export type SelectLocationFull = z.infer<typeof selectLocationFullSchema>
export type SelectLocationsFull = z.infer<typeof selectLocationsFullSchema>
export const selectInternalOptionalTerminals = z.union([selectLocationSchema, selectLocationFullSchema])
export type SelectInternalOptionalTerminals = z.infer<typeof selectInternalOptionalTerminals>

export const merakiDataSchema = z.object({
  switchNetworkId: z.string().optional(),
  edgeNetworkId: z.string().optional(),
})
export type MerakiData = z.infer<typeof merakiDataSchema>

// ── Network Status Polling ──────────────────────────────────────────

export const deviceStatusPollSchema = createSelectSchema(deviceStatusPolls)
export const insertDeviceStatusPollSchema = createInsertSchema(deviceStatusPolls)

export const deviceStatusCurrentSchema = createSelectSchema(deviceStatusCurrent)
export const insertDeviceStatusCurrentSchema = createInsertSchema(deviceStatusCurrent)
export const updateDeviceStatusCurrentSchema = createUpdateSchema(deviceStatusCurrent)

export const tunnelStatusCurrentSchema = createSelectSchema(tunnelStatusCurrent)
export const insertTunnelStatusCurrentSchema = createInsertSchema(tunnelStatusCurrent)
export const updateTunnelStatusCurrentSchema = createUpdateSchema(tunnelStatusCurrent)

export const tunnelStatusPollSchema = createSelectSchema(tunnelStatusPolls)
export const insertTunnelStatusPollSchema = createInsertSchema(tunnelStatusPolls)

export const backboneStatusCurrentSchema = createSelectSchema(backboneStatusCurrent)
export const insertBackboneStatusCurrentSchema = createInsertSchema(backboneStatusCurrent)
export const updateBackboneStatusCurrentSchema = createUpdateSchema(backboneStatusCurrent)
